import { cn } from "@/utils/helpers";
import { forwardRef } from "react";
import { kitBareFieldClass } from "@/app/routes/dashboard/components/kit/Field";

/**
 * Cockpit form primitives for the Content Calendar modals and the brief editor:
 * the kit's field (control glass at 12, accent ring on focus) on bare elements.
 */

const FIELD_BASE = cn(kitBareFieldClass, "w-full px-3.5 py-2.5 text-[14px]");

export function FieldLabel({
  children,
  htmlFor,
  className,
}: {
  children: React.ReactNode;
  htmlFor?: string;
  className?: string;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        "mb-1.5 block font-section text-[11px] font-semibold uppercase tracking-[0.12em] text-co-faint",
        className,
      )}
    >
      {children}
    </label>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  children,
  className,
}: {
  label?: string;
  htmlFor?: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col", className)}>
      {label && <FieldLabel htmlFor={htmlFor}>{label}</FieldLabel>}
      {children}
      {hint && <p className="mt-1.5 text-[12px] leading-relaxed text-co-faint">{hint}</p>}
    </div>
  );
}

export const TextInput = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => (
  <input ref={ref} className={cn(FIELD_BASE, className)} {...props} />
));
TextInput.displayName = "TextInput";

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn(FIELD_BASE, "min-h-[96px] resize-y", className)} {...props} />
));
TextArea.displayName = "TextArea";

export { default as DateFieldInput } from "@/components/form/DateInput";
export type { DateInputProps, DateInputVariant } from "@/components/form/DateInput";
export { default as FormSelect } from "@/components/form/dropdowns/FormSelect";
export type { FormSelectOption, FormSelectProps } from "@/components/form/dropdowns/FormSelect";
